import { 
  Paper, 
  Stack, 
  Typography, 
  Alert,
  Box,
  IconButton,
  Tooltip
} from "@mui/material";
import { useState, useRef } from "react";
import { UploadFile, Error, CheckCircle, Close } from "@mui/icons-material";

interface FileUploadAreaProps {
  title: string;
  subtitle?: string;
  file: File | string | null;
  fileName?: string;
  error: string;
  onFileSelect: (file: File) => void;
  onFileRemove?: () => void; // Nova prop para remover arquivo
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  disabled?: boolean;
  required?: boolean;
}

export function FileUploadArea({
  title,
  subtitle,
  file,
  fileName,
  error,
  onFileSelect,
  onFileRemove, // Nova prop
  acceptedFileTypes,
  maxFileSize = 10 * 1024 * 1024,
  disabled = false,
  required = false
}: FileUploadAreaProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [localError, setLocalError] = useState("");
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasFile = !!(file || currentFile);
  
  const getFileName = (): string => {
    if (fileName) return fileName;
    if (file instanceof File) return file.name;
    if (typeof file === 'string' && file) return file.split('/').pop() || 'Arquivo selecionado';
    if (currentFile) return currentFile.name;
    return '';
  };

  const getFileSize = (): string => {
    if (file instanceof File) {
      const size = file.size;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (currentFile) {
      const size = currentFile.size;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    }
    return '';
  };

  const validateFile = (file: File): string => {
    if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
      return "";
    }

    const fileExtension = '.' + file.name.toLowerCase().split('.').pop();
    const isValidType = acceptedFileTypes.some(type => {
      const normalizedType = type.toLowerCase();
      return fileExtension === normalizedType;
    });
    
    if (!isValidType) {
      return `Apenas arquivos ${acceptedFileTypes.join(', ')} são permitidos`;
    }

    if (file.size > maxFileSize) {
      const sizeInMB = (maxFileSize / (1024 * 1024)).toFixed(0);
      return `O arquivo é muito grande (máximo ${sizeInMB}MB)`;
    }

    return "";
  };

  const handleFileSelect = (selectedFile: File) => {
    const validationError = validateFile(selectedFile);
    
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    
    setLocalError("");
    setCurrentFile(selectedFile);
    onFileSelect(selectedFile);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Nova função para remover o arquivo
  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impede que o clique abra o seletor de arquivos
    setCurrentFile(null);
    setLocalError("");
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && !disabled) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleAreaClick = () => {
    if (!disabled && !hasFile) { // Só abre o seletor se não houver arquivo
      fileInputRef.current?.click();
    }
  };

  const getMaxFileSizeText = (): string => {
    if (maxFileSize < 1024) return `${maxFileSize} B`;
    if (maxFileSize < 1024 * 1024) return `${(maxFileSize / 1024).toFixed(2)} KB`;
    return `${(maxFileSize / (1024 * 1024)).toFixed(2)} MB`;
  };

  const displayError = error || localError;

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        {title}
        {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
      </Typography>
      
      {subtitle && (
        <Typography variant="caption" color="text.secondary" gutterBottom display="block" mb={1}>
          {subtitle}
        </Typography>
      )}
      
      <Paper
        variant="outlined"
        onClick={handleAreaClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: isDragOver ? '2px dashed #1976d2' : '2px dashed #e0e0e0',
          backgroundColor: isDragOver 
            ? 'rgba(25, 118, 210, 0.08)' 
            : disabled 
            ? 'rgba(0, 0, 0, 0.04)'
            : 'transparent',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : hasFile ? 'default' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': disabled ? {} : hasFile ? {} : {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderColor: '#1976d2'
          },
          opacity: disabled ? 0.6 : 1,
          position: 'relative'
        }}
      >
        <Stack spacing={1} alignItems="center">
          {hasFile ? (
            <>
              {onFileRemove && !disabled && (
                <Tooltip title="Remover arquivo">
                  <IconButton
                    onClick={handleRemoveFile}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      '&:hover': {
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        color: 'error.main'
                      }
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <CheckCircle color="success" fontSize="large" />
              <Typography variant="body1" color="success.main" fontWeight="medium">
                Arquivo selecionado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all', px: 2 }}>
                {getFileName()}
              </Typography>
              {getFileSize() && (
                <Typography variant="caption" color="text.secondary">
                  {getFileSize()}
                </Typography>
              )}
              {!disabled && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Clique no ✕ para remover ou arraste outro arquivo para substituir
                </Typography>
              )}
            </>
          ) : (
            <>
              <UploadFile 
                color={isDragOver ? "primary" : disabled ? "disabled" : "action"} 
                fontSize="large" 
              />
              <Typography 
                variant="body1" 
                color={isDragOver ? "primary" : disabled ? "text.disabled" : "text.primary"} 
                fontWeight="medium"
              >
                {isDragOver ? "Solte o arquivo aqui" : "Clique ou arraste o arquivo"}
              </Typography>
              <Typography variant="caption" color={disabled ? "text.disabled" : "text.secondary"}>
                {acceptedFileTypes && acceptedFileTypes.length > 0 
                  ? `Apenas arquivos ${acceptedFileTypes.join(', ')} são aceitos`
                  : 'Todos os tipos de arquivo são aceitos'
                } {maxFileSize && `(máx. ${getMaxFileSizeText()})`}
              </Typography>
            </>
          )}
        </Stack>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={acceptedFileTypes ? acceptedFileTypes.join(',') : '*'} 
          onChange={handleFileInputChange}
          disabled={disabled}
        />
      </Paper>

      {displayError && (
        <Alert 
          severity="error" 
          sx={{ mt: 1 }}
          icon={<Error fontSize="small" />}
        >
          {displayError}
        </Alert>
      )}
    </Box>
  );
}